import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import Papa from 'papaparse'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMagic, faFilePdf, faFileWord, faFileArchive, faTimes, faCheckCircle,
  faSpinner, faExclamationTriangle, faSearch,
} from '@fortawesome/free-solid-svg-icons'
import { getApiUrl, getBaseUrl, SERVERS, ENDPOINTS } from '../config/api'
import './BulkAutoDetectUpload.css'

// Mirrors UploadQuestionPapers.jsx's own cap - a sanity limit on how much work
// one batch commits the server to, not a technical ceiling.
const MAX_FILES = 50

// Same rolling 10-year window DataOperatorUpload.jsx generates for its own
// Academic Year dropdown, formatted the same way ("2025-26"). A sentinel
// option beneath it reveals a free-text field, since a new academic year
// will not be in this list yet the first time anyone uses it.
const CURRENT_YEAR = new Date().getFullYear()
const ACADEMIC_YEARS = Array.from({ length: 10 }, (_, i) => {
  const year = CURRENT_YEAR - i
  return `${year}-${(year + 1).toString().slice(-2)}`
})
const OTHER_YEAR = '__other__'

// Same list DataOperatorUpload.jsx and ReviewerAssigner.jsx use for
// Group/Department Name - the CSV Upload each department files is keyed on
// exactly (academic_year, semester, group_department_name).
const DEPARTMENTS = ['Core Eng', 'CSIS', 'Management', 'EEE']

// Same options and values as UploadQuestionPapers.jsx's own Exam
// Configuration section (the Upload Files tab), reused rather than
// reinvented so a paper filed here reads identically to one filed there.
const EXAM_TYPES = [
  ['ec1-regular', 'EC1 - Regular'], ['ec2-regular', 'EC2 - Regular'],
  ['ec3-regular', 'EC3 - Regular'], ['ec1-makeup', 'EC1 - Makeup'],
  ['ec2-makeup', 'EC2 - Makeup'], ['ec3-makeup', 'EC3 - Makeup'],
]
const EXAM_FORMATS = [['open', 'Open Book'], ['closed', 'Closed Book']]

const isPdf = (name) => (name || '').toLowerCase().endsWith('.pdf')
const isZip = (name) => (name || '').toLowerCase().endsWith('.zip')

const isRowIncomplete = (row) => !row.courseId.trim() || !row.examType || !row.examFormat

const BulkAutoDetectUpload = () => {
  // Exam type/format live per-row now, not here - a real batch for one
  // (year, semester, department) ordinarily mixes EC1/EC2/EC3 and
  // regular/makeup papers together, which is exactly the case
  // auto-detection exists for.
  const [batchFields, setBatchFields] = useState({
    academicYear: '', semester: '', department: '',
    programCommittee: '', uploaderName: '', uploaderEmail: '',
  })
  // True once the logged-in data operator's own identity has been read from
  // their session - at that point Uploader Name/Email stop being editable
  // text, since they should not be able to file an upload under someone
  // else's name. Stays false (fields stay editable) if the session lookup
  // fails, so local/dev use without a real login still works.
  const [identityLocked, setIdentityLocked] = useState(false)
  // The operator's own CSV Upload course list for this exact
  // (academicYear, semester, department) - once all three are picked, this
  // is what /scan matches covers against, ahead of the generic upstream
  // catalogue, so a matched course_id/name comes back canonical.
  const [csvCourses, setCsvCourses] = useState([])
  const [csvLookupState, setCsvLookupState] = useState('idle') // idle | loading | found | missing
  const [files, setFiles] = useState([])
  const [isScanning, setIsScanning] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [batchId, setBatchId] = useState(null)
  // [{token, filename, detected, match, error, courseId, courseName,
  //   examType, examFormat}]. Instructor is deliberately not tracked here -
  // Reviewer Assigner maps a reviewer to the course after upload, so nothing
  // in this flow needs an instructor identity up front.
  const [rows, setRows] = useState([])
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  // True once "Other" is chosen in the Academic Year dropdown, revealing a
  // free-text field for a year not in the rolling 10-year list.
  const [customYear, setCustomYear] = useState(false)
  // Which row's PDF preview is expanded, if any - one at a time, so
  // reviewing fifty files never has fifty PDFs loaded in the page at once.
  const [previewToken, setPreviewToken] = useState(null)

  // Same session identity every upload path on this page uses.
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch('/qpra/get-saml-data', {
          method: 'GET', credentials: 'include', headers: { Accept: 'application/json' },
        })
        if (!response.ok) return
        const data = await response.json()
        if (data.email) {
          setBatchFields((prev) => ({
            ...prev, uploaderName: data.name || '', uploaderEmail: data.email || '',
          }))
          setIdentityLocked(true)
        }
      } catch (err) {
        console.error('Error fetching SAML user data:', err)
      }
    }
    fetchUserData()
  }, [])

  // The CSV Upload record for this exact (year, semester, department), the
  // same lookup Reviewer Assigner does. courses_csv_data is a raw CSV string
  // with required columns course_id, course_name, dept (backend/utils/
  // csv_utils.py's NEW_REQUIRED_COURSE_COLS) - parsed client-side with the
  // same papaparse dependency already in package.json.
  useEffect(() => {
    // Read via member access, not destructuring, so every value this effect
    // depends on is expressed the same way in the body as in the deps array
    // below - destructuring `batchFields` as a whole object here made
    // react-hooks/exhaustive-deps flag it as an extra missing dependency,
    // even though the three fields it actually reads are already listed
    // individually.
    const academicYear = batchFields.academicYear
    const semester = batchFields.semester
    const department = batchFields.department
    if (!academicYear || !semester || !department) {
      setCsvCourses([])
      setCsvLookupState('idle')
      return
    }
    let cancelled = false
    const fetchCsvCourses = async () => {
      setCsvLookupState('loading')
      try {
        const url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.CSV_UPLOADS)
        const params = new URLSearchParams({
          academic_year: academicYear, semester, group_department_name: department,
        })
        const response = await fetch(`${url}?${params}`, { credentials: 'include' })
        if (!response.ok || cancelled) { setCsvLookupState('missing'); return }
        const data = await response.json()
        if (cancelled) return
        if (!data.upload) {
          setCsvCourses([])
          setCsvLookupState('missing')
          return
        }
        const parsed = Papa.parse(data.upload.courses_csv_data || '', {
          header: true, skipEmptyLines: true,
        })
        const courses = (parsed.data || [])
          .filter((row) => row.course_id && row.course_name)
          .map((row) => ({
            course_code: String(row.course_id).trim(),
            course_title: String(row.course_name).trim(),
          }))
        setCsvCourses(courses)
        setCsvLookupState(courses.length > 0 ? 'found' : 'missing')
      } catch (err) {
        console.error('Error fetching department CSV courses:', err)
        if (!cancelled) setCsvLookupState('missing')
      }
    }
    fetchCsvCourses()
    return () => { cancelled = true }
  }, [batchFields.academicYear, batchFields.semester, batchFields.department])

  // A zip stands alone - the backend rejects one mixed with other files
  // (unambiguous "how many files", same rule upload_v2.py's own zip path
  // uses), so dropping one replaces whatever was already selected, and
  // dropping more once a zip is selected is a no-op until it is removed.
  const onDrop = useCallback((accepted) => {
    const zipDropped = accepted.find((f) => isZip(f.name))
    setFiles((prev) => {
      if (zipDropped) return [zipDropped]
      if (prev.some((f) => isZip(f.name))) return prev
      return [...prev, ...accepted].slice(0, MAX_FILES)
    })
    setBatchId(null)
    setRows([])
    setResult(null)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
    },
    multiple: true,
    maxFiles: MAX_FILES,
    disabled: isScanning || isConfirming || files.length >= MAX_FILES,
  })

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((f) => f.name !== name))
    setBatchId(null)
    setRows([])
  }

  const handleBatchField = (field, value) => {
    setBatchFields((prev) => ({ ...prev, [field]: value }))
  }

  const handleScan = async () => {
    if (files.length === 0) {
      setError('Add at least one file first.')
      return
    }
    setError('')
    setIsScanning(true)
    setResult(null)
    try {
      const formData = new FormData()
      files.forEach((f) => formData.append('files', f))
      // Scope matching to the department's own CSV course list when one was
      // found - falls back to the upstream catalogue server-side otherwise.
      if (csvCourses.length > 0) {
        formData.append('course_catalogue', JSON.stringify(csvCourses))
      }
      const url = getApiUrl(SERVERS.UPLOAD, ENDPOINTS.UPLOAD_AUTO_DETECT_SCAN)
      const response = await fetch(url, { method: 'POST', credentials: 'include', body: formData })
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.detail || `Scan failed (HTTP ${response.status})`)
      }
      const data = await response.json()
      setBatchId(data.batch_id)
      // Course is filled automatically, never picked by hand: prefer the
      // backend's catalogue match (the canonical course_id/name, so the same
      // course does not fragment into slightly different strings across
      // papers) and fall back to the raw cover-detected code/title when no
      // match was found. Nothing here is user-editable any more - see the
      // "Detected on cover" column for what a row is actually filed under.
      setRows(data.items.map((item) => ({
        token: item.token,
        filename: item.filename,
        detected: item.detected,
        error: item.error,
        courseId: item.match?.matched_course_code || item.detected?.course_no || '',
        courseName: item.match?.matched_course_title || item.detected?.course_title || '',
        examType: item.guessed_exam_type || '',
        examFormat: item.guessed_exam_format || '',
      })))
    } catch (err) {
      setError(err.message || 'Could not scan these files.')
    } finally {
      setIsScanning(false)
    }
  }

  const updateRow = (token, patch) => {
    setRows((prev) => prev.map((r) => (r.token === token ? { ...r, ...patch } : r)))
  }

  const handleConfirm = async () => {
    const incomplete = rows.filter(isRowIncomplete)
    if (incomplete.length > 0) {
      setError(
        `These file(s) couldn't be matched to a course automatically, or still need an `
        + `exam type/format selected, so they can't be uploaded from here - use the `
        + `Upload Files tab for them instead: `
        + incomplete.map((r) => r.filename).join(', ')
      )
      return
    }
    const requiredBatch = [
      [batchFields.academicYear, 'Academic Year'], [batchFields.semester, 'Semester'],
      [batchFields.uploaderName, 'Uploader Name'], [batchFields.uploaderEmail, 'Uploader Email'],
    ]
    const missingBatch = requiredBatch.filter(([v]) => !v.trim()).map(([, label]) => label)
    if (missingBatch.length > 0) {
      setError('Missing required field(s): ' + missingBatch.join(', '))
      return
    }

    setError('')
    setIsConfirming(true)
    try {
      const url = getApiUrl(SERVERS.UPLOAD, ENDPOINTS.UPLOAD_AUTO_DETECT_CONFIRM)
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batch_id: batchId,
          academic_year: batchFields.academicYear,
          semester: batchFields.semester,
          program_committee: batchFields.programCommittee,
          uploader_name: batchFields.uploaderName,
          uploader_email: batchFields.uploaderEmail,
          items: rows.map((r) => ({
            token: r.token,
            course_id: r.courseId,
            course_name: r.courseName || r.courseId,
            exam_type: r.examType,
            open_closed_book: r.examFormat,
          })),
        }),
      })
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.detail || `Upload failed (HTTP ${response.status})`)
      }
      const data = await response.json()
      setResult(data)
      setFiles([])
      setRows([])
      setBatchId(null)
    } catch (err) {
      setError(err.message || 'Could not upload this batch.')
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <div className="bau-panel">
      <div className="bau-intro">
        <FontAwesomeIcon icon={faMagic} className="bau-intro-icon" />
        <div>
          <h3>Bulk Upload with Course Auto-Detection</h3>
          <p>
            Drop files from several courses at once - no course code or name needed
            up front. Each cover page is read to suggest a course; review and confirm
            before anything is saved.
          </p>
        </div>
      </div>

      {error && (
        <div className="bau-alert bau-alert-error">
          <FontAwesomeIcon icon={faExclamationTriangle} /> <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="bau-alert bau-alert-success">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>
            {result.message} Processing continues in the background - check Review Papers shortly.
          </span>
        </div>
      )}

      <div className="bau-batch-fields">
        <div className="bau-field">
          <label>Academic Year *</label>
          {customYear ? (
            <input type="text" placeholder="e.g. 2027-28" value={batchFields.academicYear}
                   onChange={(e) => handleBatchField('academicYear', e.target.value)} />
          ) : (
            <select
              value={ACADEMIC_YEARS.includes(batchFields.academicYear) ? batchFields.academicYear : ''}
              onChange={(e) => {
                if (e.target.value === OTHER_YEAR) {
                  setCustomYear(true)
                  handleBatchField('academicYear', '')
                } else {
                  handleBatchField('academicYear', e.target.value)
                }
              }}
            >
              <option value="">Select Academic Year</option>
              {ACADEMIC_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              <option value={OTHER_YEAR}>Other (new year - enter manually)</option>
            </select>
          )}
          {customYear && (
            <span className="bau-field-hint">
              <button type="button" className="bau-link-btn"
                      onClick={() => { setCustomYear(false); handleBatchField('academicYear', '') }}>
                Back to the list
              </button>
            </span>
          )}
        </div>
        <div className="bau-field">
          <label>Semester *</label>
          <select value={batchFields.semester}
                  onChange={(e) => handleBatchField('semester', e.target.value)}>
            <option value="">Select Semester</option>
            <option value="1">Semester 1</option>
            <option value="2">Semester 2</option>
          </select>
        </div>
        <div className="bau-field">
          <label>Group/Department *</label>
          <select value={batchFields.department}
                  onChange={(e) => handleBatchField('department', e.target.value)}>
            <option value="">Select Group/Department</option>
            {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          {csvLookupState === 'loading' && (
            <span className="bau-field-hint">Looking up this department's CSV Upload...</span>
          )}
          {csvLookupState === 'found' && (
            <span className="bau-field-hint bau-field-hint-ok">
              <FontAwesomeIcon icon={faCheckCircle} /> {csvCourses.length} course(s) loaded from
              CSV Upload - matching will use this list.
            </span>
          )}
          {csvLookupState === 'missing' && batchFields.department && (
            <span className="bau-field-hint bau-field-hint-warn">
              No CSV Upload found for this Academic Year/Semester/Department combination -
              matching will fall back to the general course catalogue.
            </span>
          )}
        </div>
        <div className="bau-field">
          <label>Program Committee</label>
          <input type="text" value={batchFields.programCommittee}
                 onChange={(e) => handleBatchField('programCommittee', e.target.value)} />
        </div>
        <div className="bau-field">
          <label>Uploader Name *</label>
          <input type="text" value={batchFields.uploaderName} disabled={identityLocked}
                 title={identityLocked ? 'Filed under your logged-in identity' : undefined}
                 onChange={(e) => handleBatchField('uploaderName', e.target.value)} />
        </div>
        <div className="bau-field">
          <label>Uploader Email *</label>
          <input type="email" value={batchFields.uploaderEmail} disabled={identityLocked}
                 title={identityLocked ? 'Filed under your logged-in identity' : undefined}
                 onChange={(e) => handleBatchField('uploaderEmail', e.target.value)} />
        </div>
      </div>

      {rows.length === 0 && (
        <div {...getRootProps()} className={`bau-dropzone ${isDragActive ? 'active' : ''}`}>
          <input {...getInputProps()} />
          <FontAwesomeIcon icon={faMagic} className="bau-dropzone-icon" />
          <p>Drag &amp; drop up to {MAX_FILES} question papers - or a single .zip of them - here, or click to browse</p>
          <p className="bau-dropzone-sub">PDF, DOCX, DOC, or ZIP - any mix of courses</p>
        </div>
      )}

      {rows.length === 0 && files.length > 0 && (
        <div className="bau-file-list">
          <h4>Selected Files ({files.length})</h4>
          {files.some((f) => isZip(f.name)) && (
            <p className="bau-dropzone-sub">
              Zip selected - it will be expanded into individual papers when scanned.
            </p>
          )}
          {files.map((f) => (
            <div key={f.name} className="bau-file-item">
              <FontAwesomeIcon icon={isZip(f.name) ? faFileArchive : isPdf(f.name) ? faFilePdf : faFileWord} />
              <span className="bau-file-name">{f.name}</span>
              <button type="button" onClick={() => removeFile(f.name)} aria-label="Remove file">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
          <button type="button" className="bau-btn bau-btn-primary" onClick={handleScan} disabled={isScanning}>
            {isScanning
              ? (<><FontAwesomeIcon icon={faSpinner} spin /> Reading cover pages...</>)
              : (<><FontAwesomeIcon icon={faSearch} /> Scan &amp; Detect Courses</>)}
          </button>
        </div>
      )}

      {rows.length > 0 && (
        <div className="bau-review">
          <h4>Review detected courses ({rows.length} file{rows.length === 1 ? '' : 's'})</h4>
          <p className="bau-review-hint">
            Each row files under exactly what was read off the cover (matched against the
            course list when possible). Check exam type/format, then confirm - nothing is
            saved yet. A red row means the cover page did not read cleanly enough to file
            automatically.
          </p>
          <div className="bau-table-wrap">
            <table className="bau-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Detected on cover</th>
                  <th>Exam Type</th>
                  <th>Exam Format</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const incomplete = isRowIncomplete(row)
                  const previewOpen = previewToken === row.token
                  const previewUrl =
                    `${getBaseUrl(SERVERS.UPLOAD)}/auto-detect/preview/${batchId}/${row.token}`
                  return (
                    <React.Fragment key={row.token}>
                      <tr className={incomplete ? 'bau-row-incomplete' : ''}>
                        <td className="bau-cell-file">
                          <FontAwesomeIcon icon={isPdf(row.filename) ? faFilePdf : faFileWord} />
                          {row.filename}
                          <button
                            type="button"
                            className="bau-preview-toggle"
                            onClick={() => setPreviewToken(previewOpen ? null : row.token)}
                          >
                            {previewOpen ? 'Hide preview' : 'Preview'}
                          </button>
                        </td>
                        <td className="bau-cell-detected">
                          {row.error ? (
                            <span className="bau-detect-error">{row.error}</span>
                          ) : (
                            <>
                              <div>{row.detected?.course_no || '—'}</div>
                              <div className="bau-cell-sub">{row.detected?.course_title || ''}</div>
                              {row.detected?.exam_name && (
                                <div className="bau-cell-sub">{row.detected.exam_name}</div>
                              )}
                              {row.detected?.exam_format_text && (
                                <div className="bau-cell-sub">{row.detected.exam_format_text}</div>
                              )}
                              {row.courseId ? (
                                <div className="bau-cell-filing-as">
                                  Filing as: <strong>{row.courseId}</strong>
                                  {row.courseName ? ` - ${row.courseName}` : ''}
                                </div>
                              ) : (
                                <div className="bau-detect-error">
                                  No course could be matched - cannot file this one automatically
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          <select
                            className="bau-inline-select"
                            value={row.examType}
                            onChange={(e) => updateRow(row.token, { examType: e.target.value })}
                          >
                            <option value="">Select...</option>
                            {EXAM_TYPES.map(([value, label]) =>
                              <option key={value} value={value}>{label}</option>)}
                          </select>
                        </td>
                        <td>
                          <select
                            className="bau-inline-select"
                            value={row.examFormat}
                            onChange={(e) => updateRow(row.token, { examFormat: e.target.value })}
                          >
                            <option value="">Select...</option>
                            {EXAM_FORMATS.map(([value, label]) =>
                              <option key={value} value={value}>{label}</option>)}
                          </select>
                        </td>
                      </tr>
                      {previewOpen && (
                        <tr className="bau-preview-row">
                          <td colSpan={4}>
                            <iframe
                              src={previewUrl}
                              title={`Preview of ${row.filename}`}
                              className="bau-preview-frame"
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="bau-review-actions">
            <button type="button" className="bau-btn bau-btn-secondary"
                    onClick={() => { setRows([]); setBatchId(null); setFiles([]) }}
                    disabled={isConfirming}>
              Start over
            </button>
            <button type="button" className="bau-btn bau-btn-primary"
                    onClick={handleConfirm} disabled={isConfirming}>
              {isConfirming
                ? (<><FontAwesomeIcon icon={faSpinner} spin /> Uploading...</>)
                : (<><FontAwesomeIcon icon={faCheckCircle} /> Confirm &amp; Upload All</>)}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default BulkAutoDetectUpload
